/*
 * Copyright (c) 2023 Helvia Technologies PC <contact@helvia.io>
 */
import { REFRESH_TOKEN_INTERVAL_MILLISECONDS } from "@/constants";

export class Tokens {
  private static instance: Tokens | null = null;

  private constructor() {}

  static getInstance(): Tokens {
    if (!Tokens.instance) {
      Tokens.instance = new Tokens();
    }
    return Tokens.instance;
  }

  /**
   * Decode JSON Web Token to retrieve useful information.
   *
   * @param {String} token The token string to parse.
   * @returns {Object} The final parsed object containing the token's information.
   */
  decodeJWT(token: string): Record<string, any> {
    try {
      const middlePart = token.split(".")[1].replace("-", "+").replace("_", "/");
      return JSON.parse(window.atob(middlePart));
    } catch (error) {
      return {};
    }
  }

  /**
   * Get the sub part of the token
   *
   * @returns {String} The sub part of the token
   */
  getTokenSub(token: string | undefined): string | undefined {
    if (!token) {
      return undefined;
    }
    return this.decodeJWT(token).sub;
  }

  /**
   * Sees whether a JWT has already expired
   *
   * @returns {Boolean} Whether the token has already expired
   */
  isTokenExpired(token: string | undefined): boolean | undefined {
    if (!token) {
      return undefined;
    }
    const tokenExpiration = this.decodeJWT(token).exp * 1000;
    const hasExpired = tokenExpiration < Date.now();
    return hasExpired;
  }

  /**
   * Helper function to check if the access token is about to expire.
   */
  isTokenAboutToExpire(token: string | undefined) {
    if (!token) {
      return undefined;
    }
    const tokenExpiration = this.decodeJWT(token).exp * 1000;

    const currentTime = Math.floor(Date.now());

    // Check if the token will expire in the next few seconds (e.g., 60 seconds before)
    return tokenExpiration - currentTime < REFRESH_TOKEN_INTERVAL_MILLISECONDS;
  }
}
